import type { ApolloClient } from '@apollo/client';
import { isStressDebugHooksEnabled } from 'config/stressDebugHooks';
import type { CacheTimingSpan } from './instrumentedCache';
import { InstrumentedInMemoryCache } from './instrumentedCache';

/**
 * Console-friendly handle for inspecting the live Apollo normalized cache size from the page,
 * mounted on `window.__SIRONA_APOLLO_CACHE__`. STRESS uses it to correlate cache growth with
 * heap growth and main-thread blocking across a long-lived, no-reload session (RP-6165).
 *
 * Deliberately exposes only derived counts, never the raw client or raw cache contents — the
 * cache holds real patient/report data, so this global must never make entity contents reachable
 * from `window`, even on non-production builds.
 */
export interface ApolloCacheDebugger {
  /**
   * Total entry count in the normalized cache, plus a breakdown by GraphQL typename (the
   * `Typename` prefix of each cache key, e.g. `Report`, `WorklistItem`, `ROOT_QUERY`).
   * `rootQueryFieldCount` is the number of fields on the `ROOT_QUERY` entity — grows with each
   * unique query/variable combination due to `keyArgs` in type policies (RP-6194).
   */
  getStats: () => {
    total: number;
    byTypename: Record<string, number>;
    rootQueryFieldCount: number;
  };
  /** RP-6194: drain accumulated cache timing spans. Present only when InstrumentedInMemoryCache is
   * in use (non-production builds). */
  drainSpans?: () => CacheTimingSpan[];
  /** RP-6194: current active cache-watch count. */
  getActiveWatchCount?: () => number;
  /** RP-6194: breakdown of active watchers by GraphQL operation name. */
  getWatchersByGraphqlOperationName?: () => Record<string, number>;
  /** RP-6194: `performance.timeOrigin` for aligning `performance.now()` span timestamps with
   * epoch-ms timestamps used by the STRESS harness. */
  timeOriginMs?: number;
}

const getStats = (client: ApolloClient<any>): ReturnType<ApolloCacheDebugger['getStats']> => {
  const entities = client.cache.extract();
  const byTypename: Record<string, number> = {};
  let total = 0;
  for (const key of Object.keys(entities)) {
    // extract() adds a synthetic __META entry after explicit writeQuery/writeFragment calls
    if (key === '__META') continue;
    const typename = key.split(':')[0];
    byTypename[typename] = (byTypename[typename] ?? 0) + 1;
    total++;
  }
  const rootQuery = entities['ROOT_QUERY'];
  const rootQueryFieldCount = rootQuery != null ? Object.keys(rootQuery).length : 0;
  return { total, byTypename, rootQueryFieldCount };
};

/** Whether to use the instrumented cache and mount the window global: only on non-production
 * builds whose page opted in (see config/stressDebugHooks), so ordinary dev/staging/preview
 * sessions run the same plain cache as production. */
export const isApolloCacheDebugGlobalEnabled = (): boolean => isStressDebugHooksEnabled();

/**
 * Mounts `window.__SIRONA_APOLLO_CACHE__` so opted-in STRESS sessions can read back cache entry
 * counts. No-op unless enabled and when `window` doesn't exist (SSR/tests without jsdom).
 */
export const exposeApolloCacheDebugGlobal = (client: ApolloClient<any>): void => {
  if (typeof window === 'undefined' || !isApolloCacheDebugGlobalEnabled()) return;
  const handle: ApolloCacheDebugger = { getStats: () => getStats(client) };
  const { cache } = client;
  if (cache instanceof InstrumentedInMemoryCache) {
    handle.drainSpans = () => cache.drainSpans();
    handle.getActiveWatchCount = () => cache.getActiveWatchCount();
    handle.getWatchersByGraphqlOperationName = () => cache.getWatchersByGraphqlOperationName();
    handle.timeOriginMs = performance.timeOrigin;
  }
  window.__SIRONA_APOLLO_CACHE__ = handle;
};

/** Removes the `window.__SIRONA_APOLLO_CACHE__` handle. Used by tests. */
export const clearApolloCacheDebugGlobal = (): void => {
  if (typeof window === 'undefined') return;
  delete window.__SIRONA_APOLLO_CACHE__;
};
